<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Validação Whatsapp</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Monitore processamentos, aplique filtros e acompanhe os resultados das
            validações.
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="card in cards"
          :key="card.title"
          class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">{{ card.title }}</p>
              <h3 class="mt-2 text-3xl font-medium text-gray-900">{{ card.value }}</h3>
            </div>
            <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
              <heroicon
                :name="card.icon"
                class="h-6 w-6"
                :class="card.iconClass"
                style="fill: currentColor"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="mb-6 flex justify-end">
        <router-link
          :to="`/${route}/import`"
          class="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm transition hover:shadow-md sm:w-auto"
        >
          <heroicon name="plus-circle" class="h-6 w-6 text-green-600" style="fill: currentColor" />
          <span class="text-sm font-medium text-gray-700">Importar campanha</span>
        </router-link>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div class="flex flex-col gap-1">
            <label for="filtro-inicio" class="block text-sm font-medium text-gray-700">A partir de</label>
            <input
              id="filtro-inicio"
              type="date"
              v-model="filtroInicio"
              class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="filtro-fim" class="block text-sm font-medium text-gray-700">Até</label>
            <input
              id="filtro-fim"
              type="date"
              v-model="filtroFim"
              class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div class="flex flex-col gap-1" v-if="$store.state.user.admin">
            <label for="centrocusto-validacao" class="block text-sm font-medium text-gray-700">Centro de custo</label>
            <div class="relative group">
              <select
                id="centrocusto-validacao"
                v-model="filtroCentro"
                class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
              >
                <option value="">Todos</option>
                <option
                  v-for="centro in centroscusto"
                  :key="centro._id"
                  :value="centro._id"
                >
                  {{ centro.nome }}
                </option>
              </select>
              <svg class="select-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div class="flex flex-col items-stretch gap-2 md:col-span-2 md:pt-6 sm:flex-row lg:col-span-1">
            <button
              @click="buscar"
              type="button"
              class="h-11 w-full rounded-xl font-medium text-white transition"
              :style="{ backgroundColor: primaryColor }"
              @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
              @mouseleave="$event.target.style.backgroundColor = primaryColor"
            >
              <span>Buscar</span>
            </button>

            <button
              @click="limparFiltros"
              type="button"
              class="h-11 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <span>Limpar</span>
            </button>
          </div>
        </div>
      </div>

      <div
        class="w-full overflow-hidden sm:rounded-lg border border-gray-200 bg-white shadow-sm"
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Campanha</th>
                <th
                  class="px-6 py-4 text-left font-medium"
                  v-if="$store.state.user.lzv2.centrocusto"
                >
                  Centro de custo
                </th>
                <th class="px-6 py-4 text-left font-medium">Disparo</th>
                <th class="px-6 py-4 text-center font-medium">{{ $store.getters.rotuloEnvio('Enviados', 'Enviados para fila') }}</th>
                <th class="px-6 py-4 text-center font-medium">Falhas</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
                <th class="px-6 py-4 text-center font-medium">Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="campaign in campaigns"
                :key="campaign._id"
                class="border-b border-gray-100 transition hover:bg-slate-50"
              >
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div
                      class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700"
                    >
                      {{ getCampaignInitials(campaign.nome) }}
                    </div>
                    <div class="flex flex-col">
                      <a
                        v-if="campaign.arquivoUpload && campaign.arquivoUpload.filename"
                        :href="`${apilz}/v1/campanhasLZV2/download/arquivo?filename=${campaign.arquivoUpload.filename}&originalname=${campaign.arquivoUpload.originalname}&token=${$store.state.token}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="font-medium text-gray-800 hover:text-blue-700"
                      >
                        <span
                          v-if="
                            campaign.arquivoUpload.originalname.indexOf(
                              'arquivo-modelo-lembrazap'
                            ) >= 0
                          "
                          >{{ campaign.nome }}</span
                        >
                        <span v-else>{{ campaign.arquivoUpload.originalname }}</span>
                      </a>
                      <span v-else class="font-medium text-gray-800">{{
                        campaign.nome
                      }}</span>
                      <span class="text-xs text-gray-400">Arquivo validado</span>
                    </div>
                  </div>
                </td>

                <td
                  class="px-6 font-medium text-gray-700"
                  v-if="$store.state.user.lzv2.centrocusto"
                >
                  {{
                    campaign.centrocusto && campaign.centrocusto.nome
                      ? campaign.centrocusto.nome
                      : ""
                  }}
                </td>

                <td class="px-6 text-gray-700">
                  {{ moment(campaign.dataHoraDisparo).format("DD/MM/YYYY HH:mm") }}
                </td>
                <td class="px-6 text-center font-medium text-gray-700">
                  {{ campaign.totalEnviado }}
                </td>
                <td class="px-6 text-center font-medium text-gray-700">
                  {{ campaign.totalFalhas }}
                </td>
                <td class="px-6 text-center">
                  <span
                    :class="[
                      'rounded-full px-3 py-1 text-xs font-medium',
                      returnStatus(campaign) === 'Concluída'
                        ? 'bg-green-100 text-green-700'
                        : returnStatus(campaign) === 'Em andamento'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700',
                    ]"
                  >
                    {{ returnStatus(campaign) }}
                  </span>
                </td>

                <td class="px-6 py-5">
                  <div class="flex flex-wrap justify-center gap-2">
                    <router-link
                      :to="`/${route}/detalhes/${campaign._id}`"
                      class="flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: primaryColor }"
                      @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                      @mouseleave="$event.target.style.backgroundColor = primaryColor"
                    >
                      Detalhes
                    </router-link>

                    <button
                      v-if="campaign.total > 170000"
                      type="button"
                      class="flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: mixPrimaryColor('#ffffff', 0.35) }"
                      @mouseenter="$event.target.style.backgroundColor = primaryColor"
                      @mouseleave="$event.target.style.backgroundColor = mixPrimaryColor('#ffffff', 0.35)"
                      @click="downloadResultado(campaign._id)"
                    >
                      Analítico
                    </button>

                    <a
                      v-else
                      target="_blank"
                      role="button"
                      :href="`${api}/v1/campanhasLZV2/download-resultado-url/${campaign._id}?token=${$store.state.token}`"
                      class="flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: mixPrimaryColor('#ffffff', 0.35) }"
                      @mouseenter="$event.target.style.backgroundColor = primaryColor"
                      @mouseleave="$event.target.style.backgroundColor = mixPrimaryColor('#ffffff', 0.35)"
                    >
                      Analítico
                    </a>

                    <button
                      v-if="returnPermiteExcluir(campaign)"
                      type="button"
                      class="flex h-10 items-center justify-center rounded-lg bg-red-500 px-4 text-sm font-medium text-white transition hover:bg-red-600"
                      @click="excluir(campaign._id)"
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!campaigns.length">
                <td
                  :colspan="$store.state.user.lzv2.centrocusto ? 7 : 6"
                  class="px-6 py-12 text-center text-sm text-gray-400"
                >
                  Nenhum resultado encontrado com os filtros atuais.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        :current-page="page"
        :total-pages="totalPaginas"
        :total-items="total"
        :page-size="perPage"
        item-label="validações"
        @paginate="setPage"
      />

      <div
        v-if="isLoading"
        class="fixed left-0 top-80 z-50 w-full text-center text-lg font-bold text-zinc-600"
      >
        <p class="text-zinc-200">Aguarde o fim do download!</p>
        <span class="text-green-600">{{ progresso }}</span> / 100%
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import AppPagination from "@/components/AppPagination.vue";
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  components: {
    AppPagination,
  },
  data() {
    return {
      route: "temzap",
      apilz:
        window.location.hostname === "uno.trestto.com.br"
          ? "https://uno.trestto.com.br/api"
          : process.env.VUE_APP_API,
      moment,
      campaigns: [],
      filtroInicio: moment().startOf("M").format("YYYY-MM-DD"),
      filtroFim: moment().endOf("M").format("YYYY-MM-DD"),
      filtroCentro: null,
      centroscusto: [],
      selectedCampaigns: [],
      role: "user",
      page: 1,
      perPage: 25,
      total: 0,
      isLoading: false,
      progresso: 0,
    };
  },
  computed: {
    cards() {
      const totalCampanhas = this.total;
      const totalComZap = this.campaigns.reduce(
        (sum, item) => sum + (Number(item.totalEnviado) || 0),
        0
      );
      const totalSemZap = this.campaigns.reduce(
        (sum, item) => sum + (Number(item.totalFalhas) || 0),
        0
      );
      const concluidas = this.campaigns.filter(
        (item) => this.returnStatus(item) === "Concluída"
      ).length;

      return [
        {
          title: "Total de Campanhas",
          value: totalCampanhas,
          icon: "menu",
          iconClass: "text-blue-700",
        },
        {
          title: "Contatos com WhatsApp",
          value: totalComZap.toLocaleString(),
          icon: "check-circle",
          iconClass: "text-indigo-600",
        },
        {
          title: "Contatos sem WhatsApp",
          value: totalSemZap.toLocaleString(),
          icon: "x-circle",
          iconClass: "text-red-600",
        },
        {
          title: "Concluídas",
          value: concluidas,
          icon: "check-circle",
          iconClass: "text-green-600",
        },
      ];
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    },
  },
  methods: {
    getCampaignInitials(nome) {
      if (!nome) return "--";
      return nome.slice(0, 2).toUpperCase();
    },
    buscar() {
      this.setPage(1);
    },
    limparFiltros() {
      this.filtroInicio = "";
      this.filtroFim = "";
      this.filtroCentro = this.$store.state.user.centrocusto || null;
      this.setPage(1);
    },
    async downloadResultado(campaign) {
      const response = await this.$http.get(
        `${this.apilz}/v1/campanhasLZV2/download-resultado/${campaign}`
      );

      if (response.data.success) {
        const loader = await this.$loading.show({
          container: null,
          canCancel: true,
        });

        await this.sockets.subscribe(response.data.socket_campanha, (data) => {
          this.isLoading = true;
          this.progresso = data.progress;
        });

        await this.sockets.subscribe(response.data.socket_campanha_ready, (data) => {
          console.log("READY:", data);

          this.isLoading = false;
          loader.hide();
          window.location.href = `${this.apilz}/upload?mimetype=text/csv&filename=${data.file}&folder=relatorios&original=${data.file}`;

          this.sockets.unsubscribe(response.data.socket_campanha);
          this.sockets.unsubscribe(response.data.socket_campanha_ready);
        });
      }
    },
    async excluir(id) {
      const certeza = await this.$dialog.confirm(
        "Excluir essa campanha de validação?",
        "",
        "question"
      );

      if (certeza.isOk) {
        await this.$http.delete(`/v1/campanhasLZV2/delete/${id}`);
        this.$vToastify.success("Campanha excluída com sucesso!");
        this.list();
      }
    },
    async setPage(page) {
      this.page = page;
      window.scrollTo(0, 0);

      const skip = (page - 1) * this.perPage;
      const limit = this.perPage;

      const campaignsReq = await this.$http.post("/v1/campanhasLZV2/list", {
        validacao: true,
        incluirRelatorio: false,
        centrocusto: this.filtroCentro,
        de: this.filtroInicio,
        ate: this.filtroFim,
        skip: skip,
        limit: limit,
      });

      this.campaigns = campaignsReq.data.data;
      this.total = campaignsReq.data.total;
    },
    async list() {
      this.setPage(1);
    },
    async removeSeleted() {
      const resp = await this.$http.post("/v1/campanhasLZV2/deleteall", {
        campanhas: this.selectedCampaigns,
      });
      if (!resp.data.success) {
        this.$vToastify.error(resp.data.err);
        return;
      }
      this.$vToastify.success("Campanhas removidas");
      this.list();
    },

    returnPermiteExcluir(data) {
      const today = moment();
      if (moment(data.dataHoraDisparo) > today) return true;
      else return false;
    },

    returnStatus(data) {
      const today = moment();

      if (
        data.totalEnviado === 0 &&
        data.totalFalhas === 0 &&
        moment(data.dataHoraDisparo) < today
      ) {
        return "Preparando";
      } else if (
        data.ready &&
        data.totalEnviado === 0 &&
        data.totalFalhas === 0 &&
        moment(data.dataHoraDisparo) > today
      ) {
        return "Aguardando";
      } else if (
        (data.totalEnviado > 0 || data.totalFalhas > 0) &&
        data.finalizado &&
        moment(data.dataHoraDisparo) < today
      ) {
        return "Concluída";
      } else if (data.totalEnviado > 0 || data.totalFalhas > 0) {
        return "Em andamento";
      }
    },
  },
  async beforeMount() {
    const centroscustoReq = await this.$http.post(`/v1/centrocusto/list`, { all: true });
    this.centroscusto = centroscustoReq.data.data;

    if (this.$store.state.user.centrocusto) {
      this.filtroCentro = this.$store.state.user.centrocusto;
    }

    this.list();
  },
};
</script>

<style scoped>
:deep(.heroicon-ui) {
  fill: currentColor !important;
}

#centrocusto-validacao {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

#centrocusto-validacao::-ms-expand {
  display: none;
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
